import async from 'async';

import * as config from '../../config';
import ErrorHandler from '../error_handler/error_handler';
import * as HttpUtils from '../../util/http_utils';
import * as Logger from '../../util/logger_utils';
import {ERRORS} from './medialist_errors';
import {Concurrency, Errors} from '../../modules/concurrency';

const STATES = {
    NOT_INITIALIZED: 0,
    INITIALIZING: 1,
    READY: 2,
    ACTIVE: 3
};
const MEDIA_LISTS = {
    WATCH_LIST: 'watchList',
    WATCHED_LIST: 'watchedList'
};
const UPDATE_INTERVAL = 15000;

let userId;
let contentId;
let state = STATES.NOT_INITIALIZED;
let sessionToken;
let watchListListId;
let itemId;
let mediaId;
let videoType;
let season = null;
let episode = null;
let adapter;
let intervalUpdater;
let mediaListsCache = {};
let packageName;
let isLive;
let sessionId;
let enableConcurrency;

class MediaList {

    /**
     *
     * @param {Object} options
     * @param {String} options.contentId
     * @param {String} options.userId
     * @param {Object} options.adapter
     * @param {String} options.sessionToken
     * @param {String} options.videoType
     * @param {Number} [options.episode]
     * @param {Number} [options.season]
     */
    static initialize(options) {
        const spConfig = window.sessionStorage.getItem('SPConf');
        const configuration = JSON.parse(atob(spConfig));
        contentId = options.contentId;
        userId = options.userId;
        adapter = options.adapter;
        sessionToken = options.sessionToken;
        season = options.season;
        episode = options.episode;
        videoType = videoType || options.videoType;
        mediaId = options.mediaId;
        packageName = options.packageName || configuration.packageName;
        isLive = options.isLive || configuration.isLive || false;
        sessionId = options.sessionId || configuration.sessionId;
        enableConcurrency = options.enableConcurrency || configuration.enableConcurrency || true;

        state = STATES.INITIALIZING;

        async.waterfall([
            (callback) => this._getMediaListId(MEDIA_LISTS.WATCH_LIST, (success, id) => {
                watchListListId = id;
                callback(!success);
            }),
            (callback) => this._getOrCreateWatchList((success, watchlist) => callback(!success, watchlist))
        ], function onDone(err, watchList) {
            if (err) {
                ErrorHandler.throwError(ERRORS.GENERAL_ERROR, {
                    reason: 'Did not pass the waterfall in initialize',
                    moreInfo: watchList
                });
            } else {
                state = STATES.READY;
            }
        });
    }

    static startUpdating() {
        if (state === STATES.INITIALIZING) {
            console.debug('Watchlist is initializing. Trying again in 500ms');
            setTimeout(() => this.startUpdating(), 500);
            return;
        }
        if (state === STATES.READY) {
            console.debug('start updating watchlist');
            state = STATES.ACTIVE;
            // Concurrency._concurrencyCheck((success) => {
            //     console.log(success);
            // });
            let sendHeartBeat = !(!enableConcurrency && adapter.player.isLive());
            // commenting live content not added to heart beat call
            if (sendHeartBeat) {
                this._sendUpdateWatchListRequest();
                intervalUpdater = setInterval(() => this._sendUpdateWatchListRequest(), UPDATE_INTERVAL);
            }
        } else {
            console.debug('cannot update list, state is wrong.' + state);
        }
    }

    static stopUpdating() {
        if (state !== STATES.ACTIVE) {
            console.warn('Cannot stop updating, as it is not updating');
            return;
        }
        state = STATES.READY;
        console.debug('stop updating watchlist');
        clearInterval(intervalUpdater);
    }

    /**
     * Get the progress for a series or video. If not exists, defaults will be given
     * @param {Function} callback
     * @param {Boolean} callback.success
     * @param {Object} callback.progress
     * @param {Number} callback.progress.playbackTime - defaults to 0
     * @param {Number} [callback.progress.episode] if series - defaults to 1
     * @param {Number} [callback.progress.season] if series - defaults to 1
     * @param {Object} [options]
     * @param {Object} [options.videoType]
     * @param {Object} [options.contentId]
     * @param {Object} [options.userId]
     * @param {Boolean} [options.latest]
     * @param {Number} [options.season]
     * @param {Number} [options.episode]
     * @param {Object} [options.sessionToken]
     */
    static getProgressInfo(callback, options = {}) {
        videoType = options.videoType;
        contentId = options.contentId;
        sessionToken = options.sessionToken;
        episode = options.episode;
        season = options.season;
        userId = options.userId;

        if (!videoType || !contentId) throw Error('Videotype and content need to be supplied by either initialization or options');
        if (videoType === 'movies') {
            this._getMediaList(MEDIA_LISTS.WATCH_LIST, (success, watchlist) => {
                watchlist && watchlist['playbackTime'] ? callback(success, {playbackTime: parseInt(watchlist['playbackTime'])}) : callback(success, {playbackTime: 0});
            });
        } else if (videoType === 'series') {
            this._getMediaList([MEDIA_LISTS.WATCH_LIST, MEDIA_LISTS.WATCHED_LIST], (success, mediaList) => {
                // when season/episode is undefined, the feed will find the first episode
                let progressObject = {
                    playbackTime: 0,
                    season: season,
                    episode: episode
                };

                const urlSplits = window.location.pathname && window.location.pathname.split('/');

                if (urlSplits.length > 1 && urlSplits[urlSplits.length - 1] === 'latest') {
                    options.latest = true;
                }

                let filteredMediaList = 0;
                for (let x = 0; x < mediaList.length; x++) {
                    let mList = mediaList[x];

                    if (mList.context === 'watchedList') {
                        filteredMediaList = mList;
                    }

                    if (mList.context === 'watchList') {
                        filteredMediaList = mList;
                        break;
                    }
                }

                mediaList = filteredMediaList;

                if (mediaList) {
                    const parsedSeason = mediaList['seasonNumber'] && parseInt(mediaList['seasonNumber']);
                    const parsedEpisode = mediaList['episodeNumber'] && parseInt(mediaList['episodeNumber']);
                    const parsedTime = mediaList['playbackTime'] && parseInt(mediaList['playbackTime']);

                    if (options.latest) {
                        progressObject['playbackTime'] = parsedTime;
                        progressObject['season'] = parsedSeason;
                        progressObject['episode'] = parsedEpisode;
                    } else {
                        progressObject['season'] = options.season || parsedSeason;
                        progressObject['episode'] = options.episode || parsedEpisode;

                        if (parseInt(parsedSeason) === parseInt(options.season) &&
                                parseInt(parsedEpisode) === parseInt(options.episode)) { // Ignore time if it is for another episode
                            progressObject['playbackTime'] = parsedTime;
                        }
                    }
                }
                callback(success, progressObject);
            });
        }
    }

    /**
     * Removes the current item from the watch list and adds it to the watchedlist
     * @param [nextSeason]
     * @param [nextEpisode]
     */
    static moveToWatchedList(nextSeason, nextEpisode) {

        if (videoType === 'series' && nextSeason && nextEpisode) {
            episode = nextEpisode;
            season = nextSeason;

            this._sendUpdateWatchListRequest({
                fromStart: true
            });
        } else {
            // todo: make watchedlist work with portal for series. Currently watched items are not seen as resume.

            this._deleteMediaListItem(itemId);

            this._getMediaListId(MEDIA_LISTS.WATCHED_LIST, (success, body) => {
                const watchedListId = this._getJsonFromESBReply(success, body);
                this._createMediaListItem(watchedListId, (success, res) => {
                    this._getJsonFromESBReply(success, res);
                });
            });
        }
    }

    /**
     * @param callback
     * @return {Object} watchList
     */
    static _getOrCreateWatchList(callback) {
        let watchlistFound = false;

        this._getMediaListsForContent((success, mediaLists) => {
            if (mediaLists && mediaLists.length > 0) {
                for (let i = 0; i < mediaLists.length; i++) {
                    const mediaList = mediaLists[i];

                    if (mediaList['context'] === MEDIA_LISTS.WATCH_LIST && !watchlistFound) {
                        itemId = mediaList['itemId'];
                        watchlistFound = true;
                        callback(true, mediaList);
                    } else {
                        this._deleteMediaListItem(mediaList['itemId']);
                    }
                }
            }

            if (!watchlistFound) {
                this._createMediaListItem(watchListListId, (success, watchlist) => {
                    itemId = watchlist['itemId'];
                    callback(true, watchlist);
                });
            }
        });
    }

    /**
     * Creates a watchlist
     * @param listId
     * @param {Function} callback
     * @param {Boolean} callback.success
     * @param {Object} callback.watchlist
     * @private
     */
    static _createMediaListItem(listId, callback) {
        let body = {
            'index': '1',
            'titleId': `http://data.entertainment.tv.theplatform.eu/entertainment/data/Program/${contentId}`,
            'playbackTime': 0,
            'programType': videoType,
            'rankOrder': 0
        };

        if (videoType === 'series') {
            body = Object.assign(body, {
                'seasonNumber': season,
                'episodeNumber': episode
            });
        }

        HttpUtils.post({
            uri: `${config.esbUrl}userAccounts/${userId}/mediaLists/${listId}/items`,
            headers: {
                Authorization: `Bearer ${sessionToken}`,
                'client-type': 'website',
                'xtype': 'portal'
            },
            body: body
        }, (success, body) => {
            callback(true, this._getJsonFromESBReply(success, body));
        });
    }

    /**
     * Get the watchlist ID for the by ${userId} specified user.
     * @param {String} mediaListName - the mediaList to look for, uses const MEDIA_LISTS
     * @param {Function} callback
     * @param {boolean} callback.success
     * @param {string} callback.watchListListId
     */
    static _getMediaListId(mediaListName, callback) {
        HttpUtils.get({
            uri: `${config.esbUrl}userAccounts/${userId}/mediaLists`,
            headers: {
                Authorization: `Bearer ${sessionToken}`,
                'client-type': 'website',
                'xtype': 'portal'
            }
        }, (success, body) => {
            const mediaLists = this._getJsonFromESBReply(success, body);

            if (mediaLists.length > 0) {
                for (let i = 0; i < mediaLists.length; i++) {
                    if (mediaLists[i].context === mediaListName) {
                        return callback(true, mediaLists[i].id);
                    }
                }
            } else {
                ErrorHandler.throwError(ERRORS.WATCHLIST_DOES_NOT_EXIST, {
                    reason: 'Watchlist does not exist, so could not fetch ID',
                    availableMediaLists: mediaLists
                });
            }
        });
    }

    static _deleteMediaListItem(itemId, callback) {
        HttpUtils.delete({
            uri: `${config.esbUrl}userAccounts/${userId}/mediaLists/${watchListListId}/items/${itemId}`,
            headers: {
                Authorization: `Bearer ${sessionToken}`,
                'client-type': 'website',
                'xtype': 'portal'
            }
        }, (success) => {
            callback && callback(success);
        });
    }

    /**
     * Returns the current progress of the video
     * @return {*}
     */
    static _getCurrentDuration() {
        if ([STATES.READY, STATES.ACTIVE, STATES.STOPPED].indexOf(state) === -1) {
            console.error('Cannot get duration because state is invalid');
            return 0;
        }
        const duration = parseInt(adapter.getCurrentTime());
        return isNaN(duration) ? 0 : duration;
    }

    /**
     *
     * @param [options]
     * @param {Number} [options.fromStart=0] from what progress
     * @private
     */
    static _sendUpdateWatchListRequest(options = {}) {

        const headers = {
            Authorization: `Bearer ${sessionToken}`,
            'client-type': 'website',
            'xtype': 'portal'
        };
        isLive = adapter.player.isLive() || false;
        let body = {
            media_id: `${mediaId}`,
            playback_time: options.fromStart ? 0 : this._getCurrentDuration(),
            origin: 'website',
            sessID: sessionId || '9999999999',
            deviceType: 'website',
            package: packageName || 'starzplay',
            isLive: isLive
        };

        if (videoType == 'series') {
            body.season_number = season;
            body.episode_number = episode;
            body.series_id = `${itemId}`;
        }

        HttpUtils.post({
            uri: `${config.recEngineUrl}users/${userId}/media/events/heartbeat?isLive=${isLive}`,
            body: body,
            headers: headers
        }, function onReply(success, data) {
            if (!success) {
                console.error('Could not update watchlist.', data);
            }
        });
    }

    /**
     * Get the media lists. !!! Caches the request by Id, so it won't do twice the same call.
     * @param {Function} callback
     * @param {boolean} callback.success
     * @param {object} callback.mediaLists - all lists that are linked to this content Id, like: wishList, watchList, watchedList.
     */
    static _getMediaListsForContent(callback) {
        if (mediaListsCache[contentId]) return callback(true, mediaListsCache[contentId]);
        HttpUtils.get({
            uri: `${config.esbUrl}userAccounts/${userId}/mediaLists/items/${contentId}`,
            headers: {
                Authorization: `Bearer ${sessionToken}`,
                'client-type': 'website',
                'xtype': 'portal'
            }
        }, (success, body) => {
            mediaListsCache[contentId] = this._getJsonFromESBReply(success, body);
            callback(success, mediaListsCache[contentId]);
        });
    }

    /**
     * ESB JSON Reply Validator
     *
     * @param success
     * @param body
     *
     * @return {Object} parsedReply
     * @throws error to Error Handler on invalid JSON or unsuccesful request
     * @private
     */
    static _getJsonFromESBReply(success, body) {
        if (!success) {
            ErrorHandler.throwError(ERRORS.INVALID_REPLY_ESB, {
                reason: 'Invalid Reply from ESB: Request failed',
                response: body
            });
        }

        try {
            if (typeof body === 'object') return body;
            return JSON.parse(body);
        } catch (e) {
            Logger.error(e);
            ErrorHandler.throwError(ERRORS.INVALID_REPLY_ESB, {
                reason: 'Invalid Reply from ESB: Request is not a JSON',
                response: body
            });
        }
    }

    /**
     *
     * @param {Array|String} mediaListTypes
     * @param {Function} callback
     * @param {Boolean} callback.success
     * @param {Object} callback.mediaList
     * @private
     */
    static _getMediaList(mediaListTypes, callback) {
        mediaListTypes = typeof mediaListTypes === 'string' ? [mediaListTypes] : mediaListTypes;
        let objValues = [];
        this._getMediaListsForContent((success, mediaLists) => {
            if (mediaLists && mediaLists.length > 0) {
                for (let i = 0; i < mediaLists.length; i++) {
                    const mediaList = mediaLists[i];
                    if (mediaListTypes.indexOf(mediaList['context']) > -1) {
                        objValues.push(mediaList);
                    }
                }
            }

            // This is the case for movies, they do not really get affected by watchList or watchedList
            objValues = mediaListTypes.length == 1 && objValues[0] || objValues;

            return callback(true, objValues);
        });

    }
}

module.exports = MediaList;



// WEBPACK FOOTER //
// ./src/modules/media_list/media_list.js